'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  bookTitle: string;
  authorName: string;
  genre: string;
  publicationDate: string;
  synopsis: string;
  arcLink: string;
  contactEmail: string;
  additionalInfo: string;
}

const genres = [
  'Fiction',
  'Science Fiction',
  'Fantasy',
  'Mystery',
  'Thriller',
  'Historical Fiction',
  'Romance',
  'Literary Fiction',
  'Non-Fiction',
  'Biography/Memoir',
  'Self-Help',
  'Young Adult',
  'Horror',
  'Poetry',
  'Other',
];

export default function SubmissionForm() {
  const [formData, setFormData] = useState<FormData>({
    bookTitle: '',
    authorName: '',
    genre: '',
    publicationDate: '',
    synopsis: '',
    arcLink: '',
    contactEmail: '',
    additionalInfo: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    // Reset form after 3 seconds
    setTimeout(() => {
      setSubmitSuccess(false);
      setFormData({
        bookTitle: '',
        authorName: '',
        genre: '',
        publicationDate: '',
        synopsis: '',
        arcLink: '',
        contactEmail: '',
        additionalInfo: '',
      });
    }, 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <section className="py-20 px-6 bg-card/30">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mb-6 tracking-tight">
            Submit Your Book
          </h2>
          <p className="text-muted-foreground font-light">
            Fill out the form below to request an ARC review
          </p>
        </div>

        <form onSubmit={handleSubmit} className="glass-card p-8 md:p-12 rounded-xl space-y-8">
          {/* Book Title */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-3">
              Book Title <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              name="bookTitle"
              value={formData.bookTitle}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-background/50 border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
              placeholder="Enter the full title of your book"
            />
          </div>

          {/* Author Name */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-3">
              Author Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              name="authorName"
              value={formData.authorName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-background/50 border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
              placeholder="Your name or pen name"
            />
          </div>

          {/* Genre & Publication Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-3">
                Genre <span className="text-destructive">*</span>
              </label>
              <select
                name="genre"
                value={formData.genre}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg bg-background/50 border border-border text-foreground focus:outline-none focus:border-primary transition-colors"
              >
                <option value="">Select a genre</option>
                {genres.map((genre) => (
                  <option key={`genre_option_${genre}`} value={genre}>
                    {genre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-3">
                Publication Date <span className="text-destructive">*</span>
              </label>
              <input
                type="date"
                name="publicationDate"
                value={formData.publicationDate}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg bg-background/50 border border-border text-foreground focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          {/* Synopsis */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-3">
              Book Synopsis <span className="text-destructive">*</span>
            </label>
            <textarea
              name="synopsis"
              value={formData.synopsis}
              onChange={handleChange}
              required
              rows={6}
              className="w-full px-4 py-3 rounded-lg bg-background/50 border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors resize-none"
              placeholder="Provide a brief synopsis (200-300 words)"
            />
          </div>

          {/* ARC Link */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-3">
              ARC Link or Attachment Info <span className="text-destructive">*</span>
            </label>
            <input
              type="url"
              name="arcLink"
              value={formData.arcLink}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-background/50 border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
              placeholder="NetGalley link, Dropbox, Google Drive, etc."
            />
            <p className="text-xs text-muted-foreground mt-2">
              Provide a link to download the ARC (ePub, PDF, or Kindle format preferred)
            </p>
          </div>

          {/* Contact Email */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-3">
              Contact Email <span className="text-destructive">*</span>
            </label>
            <input
              type="email"
              name="contactEmail"
              value={formData.contactEmail}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-background/50 border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
              placeholder="your.email@example.com"
            />
          </div>

          {/* Additional Info */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-3">
              Additional Information (Optional)
            </label>
            <textarea
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 rounded-lg bg-background/50 border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors resize-none"
              placeholder="Any other details you'd like to share (comp titles, trigger warnings, etc.)"
            />
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-center pt-4">
            <button
              type="submit"
              disabled={isSubmitting || submitSuccess}
              className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground animate-spin" />
                  Submitting...
                </>
              ) : submitSuccess ? (
                <>
                  <Icon name="CheckCircleIcon" size={20} />
                  Submitted Successfully!
                </>
              ) : (
                <>
                  Submit ARC Request
                  <Icon name="ArrowRightIcon" size={20} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}